import React, { FC, useMemo } from "react";

import { Icon, Stack, Text } from "@bookingcom/bui-react";
import { useI18n, I18nChildContext } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";

import { UIProductType, UIProductCabinBaggagePerTraveller, UIProductCheckedInBaggageOption } from "@flights/types";
import { productIcons } from "@flights/branded-fares";
import FlightDetailsAncillaryItemLayout from "../../../../components/elements/FlightDetailsAncillaryItemLayout";
import useFormatPrice from "../../../../hooks/useFormatPrice";
import { LoadingAncillariesPrice } from "./LoadingAncillariesPrice";
import styles from "./BaggageItem.module.css";
import { formattingUtils } from "@flights/web-api-utils-universal";
import { FlightCardBadge } from "../../../../components/elements/FlightCard/components/FlightCardBadge";
import { getCampaignBadgesConfig } from "../../../../utils/flightCampaignConfig";
import FlightDetailsAncillaryItemLayoutAlt from "../../../../components/elements/FlightDetailsAncillaryItemLayoutAlt";
import { useSelector } from "react-redux";
import { getPreOptedAncillaries } from "../../../store/extraProducts/selectors";
import { useRyanAirAncillaryPriceLabel } from "../../../../hooks/useRyanAirAncillaryPriceLabel";

export const BaggageItem: FC<{
  type: UIProductType;
  product: UIProductCabinBaggagePerTraveller | UIProductCheckedInBaggageOption;
  hideIncludedSection?: boolean;
  showLoadingPrice?: boolean;
  dataTestId?: string;
  hasGeniusDeal?: boolean;
  /* start - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
  condensedFDDesktop?: boolean;
  condensedFDMobile?: boolean;
  /* end - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
}> = ({
  type,
  product,
  hideIncludedSection,
  showLoadingPrice,
  dataTestId,
  hasGeniusDeal,
  condensedFDDesktop,
  condensedFDMobile
}) => {
  const i18n = useI18n() as I18nChildContext;
  const { formatPrice, startingPrice } = useFormatPrice();
  const preOptedAncillaries = useSelector(getPreOptedAncillaries);
  const preOptedCheckedBaggage = preOptedAncillaries?.checkedInBaggage;
  const { priceBreakdown, luggageAllowance } = product;

  const geniusDealAncillaryBadges =
    "badges" in product
      ? getCampaignBadgesConfig(product?.badges).filter((badge) => badge.style !== "GENIUS")
      : undefined;
  const price: string = priceBreakdown ? startingPrice(priceBreakdown.total) : "";
  const priceStrikethrough =
    priceBreakdown?.showPriceStrikethrough && priceBreakdown?.totalWithoutDiscount
      ? formatPrice(priceBreakdown.totalWithoutDiscount)
      : undefined;
  const priceStrikethroughA11yText = !!priceStrikethrough
    ? i18n.trans(
        t("a11y_flights_fd_genius_checked_bag_baggage_available_from_price", {
          variables: {
            price: formatPrice(priceBreakdown.total),
            old_price: priceStrikethrough
          }
        })
      )
    : undefined;

  const IconSVG = productIcons[type];

  const hasIncludedBaggage = Boolean(type === "checkedInBaggage" && preOptedCheckedBaggage);
  const pieces = hasIncludedBaggage ? luggageAllowance.maxPiece : undefined;
  const weightCopy = hasIncludedBaggage
    ? formattingUtils.luggageAllowanceCopyV2(luggageAllowance, i18n).weightPerPiece
    : undefined;

  const { getRyanAirPriceLabel } = useRyanAirAncillaryPriceLabel();
  const ryanAirPriceLabel = getRyanAirPriceLabel(priceBreakdown);

  const statusBasedPriceDisplay = useMemo(() => {
    if (!showLoadingPrice) {
      return (
        <Stack
          direction="row"
          alignItems="center"
          attributes={!!priceStrikethroughA11yText ? { "aria-label": priceStrikethroughA11yText } : {}}
        >
          <Text
            attributes={{ "data-testid": `${dataTestId}_price`, "aria-hidden": !!priceStrikethroughA11yText }}
            variant="body_2"
          >
            {price}
          </Text>
          {priceStrikethrough && (
            <Text
              attributes={{ "aria-hidden": !!priceStrikethroughA11yText }}
              variant="body_2"
              className={styles.priceStrikethrough}
            >
              {priceStrikethrough}
            </Text>
          )}
        </Stack>
      );
    }

    return (
      <LoadingAncillariesPrice price={price} productTitle={getBaggageProductTitle(type, i18n, pieces)} type={type} />
    );
  }, [price, type, pieces, priceStrikethrough, priceStrikethroughA11yText, i18n, showLoadingPrice, dataTestId]);

  /* start - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
  const isCondensed = condensedFDDesktop || condensedFDMobile;

  const titleAndBadgesContent = (
    <Stack direction={"row"} gap={condensedFDDesktop ? 1 : undefined}>
      <Text attributes={{ "data-testid": `${dataTestId}_title` }} variant="body_2">
        {getBaggageProductTitle(type, i18n, pieces)}
      </Text>
      {hasGeniusDeal && geniusDealAncillaryBadges && geniusDealAncillaryBadges.length > 0 && (
        <div className={condensedFDDesktop ? undefined : styles.badges}>
          {geniusDealAncillaryBadges?.map((badge, idx) => (
            <FlightCardBadge key={`${badge.style}-${badge.text || idx}`} {...badge} />
          ))}
        </div>
      )}
    </Stack>
  );

  return isCondensed ? (
    <FlightDetailsAncillaryItemLayoutAlt
      icon={<Icon svg={IconSVG} size="large" />}
      leftColumnContent={
        <div aria-live="polite" aria-relevant="removals">
          {titleAndBadgesContent}
        </div>
      }
      rightColumnContent={
        <div aria-live="polite" aria-relevant="removals">
          <Stack direction="column" alignItems="end" gap={0} wrap="nowrap">
            <Stack
              direction={condensedFDMobile ? "column" : "row"}
              alignItems={condensedFDMobile ? "end" : "center"}
              gap={1}
              wrap="nowrap"
              attributes={!!priceStrikethroughA11yText ? { "aria-label": priceStrikethroughA11yText } : {}}
            >
              {priceStrikethrough && (
                <Text
                  variant={condensedFDMobile ? "small_1" : "body_2"}
                  className={styles.priceStrikethrough}
                  attributes={{ "aria-hidden": !!priceStrikethroughA11yText }}
                >
                  {priceStrikethrough}
                </Text>
              )}
              <Text
                attributes={{ "data-testid": `${dataTestId}_price` }}
                variant={condensedFDMobile ? "small_1" : "body_2"}
              >
                {luggageAllowance.maxPiece > 1
                  ? i18n.trans(
                      t("flights_fd_extras_condensed_checked_bag_each", {
                        variables: { price: formatPrice(priceBreakdown.total) }
                      })
                    )
                  : i18n.trans(
                      t("flights_fd_extras_condensed_checked_bag_one", {
                        variables: { price: formatPrice(priceBreakdown.total) }
                      })
                    )}
              </Text>
            </Stack>
            {ryanAirPriceLabel && (
              <Text
                attributes={{ "data-testid": `${dataTestId}_ryanair_price_breakdown` }}
                color="neutral_alt"
                variant="small_1"
                className={styles.ryanAirPriceLabelCondensed}
              >
                {ryanAirPriceLabel}
              </Text>
            )}
          </Stack>
        </div>
      }
    />
  ) : (
    /* end - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */

    <FlightDetailsAncillaryItemLayout
      icon={<Icon svg={IconSVG} size="large" />}
      content={
        <div aria-live="polite" aria-relevant="removals">
          {titleAndBadgesContent}
          {weightCopy ? (
            <Text attributes={{ "data-testid": `${dataTestId}_price` }} variant="body_2">
              {weightCopy}
            </Text>
          ) : (
            statusBasedPriceDisplay
          )}
          {ryanAirPriceLabel ? (
            <Text attributes={{ "data-testid": `${dataTestId}_ryanair_price_breakdown` }} color={"neutral_alt"}>
              {ryanAirPriceLabel}
            </Text>
          ) : undefined}
        </div>
      }
      includedBaggage={hasIncludedBaggage}
      hideIncludedSection={hideIncludedSection}
    />
  );
};

const getBaggageProductTitle = (type: UIProductType, i18n: I18nChildContext, maxPiece?: number): string => {
  switch (type) {
    case "cabinBaggage":
    case "cabinBaggagePerTraveller":
      return maxPiece
        ? i18n.trans(
            t("flights_baggage_details_count_cabin", {
              variables: { num_exception: maxPiece, num_bags: maxPiece }
            })
          )
        : i18n.trans(t("flights_ancillary_baggage_cabin"));

    case "checkedInBaggage":
      return maxPiece
        ? i18n.trans(
            t("flights_baggage_details_count_checked", {
              variables: { num_exception: maxPiece, num_bags: maxPiece }
            })
          )
        : i18n.trans(t("flights_ancillary_baggage_checked"));

    default:
      throw Error(`Unknown baggage type: ${type}`);
  }
};
